import { components as c } from "@/components";
import {
	useCountBySettingsQuery,
	useDiariesBySettingsQuery,
} from "@/features/diaries/api/diaryQueries";
import { tableComponents as tc } from "@/features/patients/features/overview";
import { cn } from "@/lib/utils";
import type { PatientsSearchT } from "@/routes/_app/patients";
import type { DiariesCountT } from "@/types/database/diaries";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useProfileQuery } from "../../../../../profile/api/selectProfile";
import useDiariesTable from "../hooks/useDiariesTable";
import DiariesTable from "./DiariesTable";
import TableMenu from "./TableMenu";

interface Props {
	settings: PatientsSearchT;
}
function PatientsTable({ settings }: Props) {
	const {
		data: diaries,
		isFetching,
		refetch: refetchPatientsData,
	} = useDiariesBySettingsQuery(settings);
	const { data: diariesCount } = useCountBySettingsQuery(settings);

	const { data: profile } = useProfileQuery();

	const getCount = (
		obj: DiariesCountT | undefined,
		key: string | undefined,
	): number => {
		if (!obj || !key) return 0;
		return Object.prototype.hasOwnProperty.call(obj, key)
			? obj[key as keyof DiariesCountT]
			: 0;
	};

	const [count, setCount] = useState<number>(0);

	const { table, paginationRange } = useDiariesTable({
		diaries: diaries ?? [],
	});

	useEffect(() => {
		const newCount = getCount(diariesCount, settings.tab);
		setCount(newCount);
	}, [diariesCount, settings.tab, getCount]);

	return (
		<div className="w-full h-full">
			<div className="flex flex-row flex-wrap-reverse justify-between gap-2 my-2">
				<div className="flex justify-between md:justify-center items-center md:w-fit">
					<tc.StateTabs tab={settings.tab} diariesCount={diariesCount} />
					<c.ui.Button
						size="icon"
						variant="ghost"
						disabled={isFetching}
						className={cn(isFetching && "animate-spin")}
						onClick={() => refetchPatientsData()}
					>
						<RefreshCw size={20} />
					</c.ui.Button>
				</div>

				<div className="flex justify-between md:justify-center items-center gap-x-2 w-full md:w-fit">
					<tc.Search />
					<TableMenu />
				</div>
			</div>
			<DiariesTable table={table} />
			<div className="flex justify-end items-center space-x-2 py-4">
				<div className="flex-1 text-muted-foreground text-sm">
					{count === 0
						? "No diaries on this page"
						: `Showing diaries ${paginationRange.start} to ${paginationRange.end} of ${paginationRange.total}`}
				</div>
				<div className="flex space-x-2">
					<c.ui.Button
						variant="outline"
						size="sm"
						onClick={table.previousPage}
						disabled={!table.getCanPreviousPage()}
					>
						Previous
					</c.ui.Button>
					<c.ui.Button
						variant="outline"
						size="sm"
						onClick={table.nextPage}
						disabled={!table.getCanNextPage()}
					>
						Next
					</c.ui.Button>
				</div>
			</div>
		</div>
	);
}

export default PatientsTable;
